import type {
  CellClassParams,
  CellStyle,
  ColDef,
  ValueFormatterParams,
  ValueParserParams,
} from 'ag-grid-community'
import type { OrderPositionDto } from '@crm/shared'
import { Badge } from '../../../shared/ui/Badge'
import { formatMoney, formatPrice } from '../orderFormat'

const CURRENCY_SIGN: Record<string, string> = { RUB: '₽', USD: '$', EUR: '€', CNY: '¥' }

function costText(p: OrderPositionDto): string {
  if (p.costPrice == null) return '—'
  const sign = p.costCurrency ? (CURRENCY_SIGN[p.costCurrency] ?? p.costCurrency) : ''
  return `${p.costPrice.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ${sign}`.trim()
}

/** Строка таблицы «Позиции заказа». */
export type PosRow = {
  requestItemId: string
  num: number
  partNumber: string
  category: string
  brand: string
  matrix: string
  supplier: string
  note: string
  cost: string
  quantity: number
  price: number
  sum: number
  handlerName: string
  handlerKind: 'procurement' | 'manager'
  /** «в счёте № …» — позиция в счёте, загруженном из 1С; «ждёт счёта» — нет (21.10.2026). */
  invoice1c: string
}

export function toPosRow(p: OrderPositionDto): PosRow {
  return {
    requestItemId: p.requestItemId,
    num: p.position,
    partNumber: p.partNumber,
    category: p.category ?? '—',
    brand: p.brand ?? '',
    matrix: p.matrix ?? '—',
    supplier: p.supplierName ?? '—',
    note: p.note || '—',
    cost: costText(p),
    quantity: p.quantity,
    price: p.customerPrice,
    sum: p.sum,
    handlerName: p.handlerName ?? '—',
    handlerKind: p.handlerKind,
    invoice1c: p.awaitingInvoice
      ? 'ждёт счёта'
      : p.invoiceNumber
        ? `в счёте № ${p.invoiceNumber}`
        : 'в счёте',
  }
}

const moneyFmt = (p: ValueFormatterParams) =>
  p.value == null ? '' : formatMoney(p.value as number)

// Цена клиенту — полная, до 4 знаков: сумма строки считается от неё.
const priceFmt = (p: ValueFormatterParams) =>
  p.value == null ? '' : formatPrice(p.value as number)

// Ввод числа: срезает «₽» и пробелы-разряды, запятая — десятичный разделитель.
// Невалидный ввод → прежнее значение (ячейка откатится).
// Стёртая ячейка — тоже откат (раньше Number('') = 0 сохранял цену нулём).
export const numParser = (p: ValueParserParams): number => {
  const raw = String(p.newValue ?? '')
    .replace(/[^\d.,-]/g, '')
    .replace(',', '.')
  const n = raw === '' ? NaN : Number(raw)
  return Number.isFinite(n) ? n : (p.oldValue as number)
}

/**
 * Колонки «Позиций заказа». canEdit — заказ можно менять (до «Оплачен», есть
 * права): артикул, бренд, количество и цена клиенту правятся в ячейке
 * (двойной клик или Enter) и подсвечены жёлтым. Подсветка — inline-стилем
 * поверх getCellStyle (выделение диапазона): его прозрачный фон перебил бы класс.
 * wrapText+autoHeight у текстовых колонок — текст виден целиком, без «…».
 */
export function buildOrderPositionColDefs(
  canEdit: boolean,
  getCellStyle: (p: CellClassParams) => CellStyle,
): ColDef<PosRow>[] {
  const edit = canEdit ? 'cursor-text' : ''
  const editStyle = canEdit
    ? {
        cellStyle: (p: CellClassParams) => {
          const s = getCellStyle(p)
          return s.background === 'transparent' ? { background: 'var(--crm-edit-cell-bg)' } : s
        },
      }
    : {}
  return [
    {
      field: 'num',
      headerName: '№',
      width: 64,
      minWidth: 56,
      filter: false,
      cellClass: 'text-gray-500',
    },
    {
      field: 'partNumber',
      headerName: 'Артикул',
      minWidth: 150,
      wrapText: true,
      autoHeight: true,
      editable: canEdit,
      cellClass: edit,
      ...editStyle,
    },
    {
      field: 'category',
      headerName: 'Наименование',
      minWidth: 130,
      wrapText: true,
      autoHeight: true,
    },
    {
      field: 'brand',
      headerName: 'Бренд',
      minWidth: 130,
      wrapText: true,
      autoHeight: true,
      editable: canEdit,
      cellClass: edit,
      ...editStyle,
      valueFormatter: (p) => (p.value ? String(p.value) : '—'),
    },
    { field: 'matrix', headerName: 'Матрица', minWidth: 110 },
    {
      field: 'supplier',
      headerName: 'Поставщик',
      minWidth: 150,
      wrapText: true,
      autoHeight: true,
    },
    { field: 'note', headerName: 'Заметка', minWidth: 180, wrapText: true, autoHeight: true },
    {
      field: 'cost',
      headerName: 'Себест.',
      minWidth: 100,
      filter: false,
      cellClass: 'text-right text-gray-400',
      headerClass: 'ag-right-aligned-header',
    },
    {
      field: 'quantity',
      headerName: 'Кол-во',
      minWidth: 88,
      filter: false,
      editable: canEdit,
      cellClass: `text-right ${edit}`,
      ...editStyle,
      headerClass: 'ag-right-aligned-header',
      valueParser: numParser,
    },
    {
      field: 'price',
      headerName: 'Цена клиенту',
      minWidth: 120,
      filter: false,
      editable: canEdit,
      cellClass: `text-right font-medium ${edit}`,
      ...editStyle,
      headerClass: 'ag-right-aligned-header',
      valueFormatter: priceFmt,
      valueParser: numParser,
    },
    {
      field: 'sum',
      headerName: 'Сумма',
      minWidth: 120,
      filter: false,
      cellClass: 'text-right',
      headerClass: 'ag-right-aligned-header',
      valueFormatter: moneyFmt,
    },
    {
      field: 'invoice1c',
      headerName: 'Счёт 1С',
      minWidth: 120,
      cellRenderer: ({ data }: { data: PosRow }) => (
        <Badge color={data.invoice1c.startsWith('в счёте') ? 'green' : 'yellow'}>
          {data.invoice1c}
        </Badge>
      ),
    },
    {
      field: 'handlerName',
      headerName: 'Сопровод',
      minWidth: 150,
      cellRenderer: ({ data }: { data: PosRow }) => (
        <span
          className={`inline-block rounded px-1.5 py-0.5 text-xs ${
            data.handlerKind === 'procurement'
              ? 'bg-orange-100 text-orange-800'
              : 'bg-sky-100 text-sky-800'
          }`}
        >
          {data.handlerName}
        </span>
      ),
    },
  ]
}
